import React from 'react';
import { Collapse, List, Space, Typography } from 'antd';
import {
    CheckSquareOutlined,
    PlusOutlined,
    ToolOutlined,
    WarningOutlined
} from '@ant-design/icons';
import Button from "@common/Button";
import EditableSectionDevelopment from "./EditableSectionDevelopment";

const { Panel } = Collapse;
const { Title, Text, Paragraph } = Typography;

const PlanningConsiderationsSection = ({
    lessonContent,
    showComponents,
    isEditMode,
    editingState,
    adaptedKeys,
    handleEdit,
    handleContentChange,
    handleSave,
    handleCancel,
    handleAddListItem,
    handleRemoveListItem,
    handleClearField
}) => {
    return (
        <div id="lesson-planning">
            <Title level={2}>Planning Considerations</Title>
            <Collapse defaultActiveKey={['1']}>
                {showComponents.has('corrective_plan') && (
                    <Panel header={<Space><WarningOutlined />Corrective Instruction Plan</Space>} key="1">
                        <EditableSectionDevelopment
                            contentKey='correctiveInstructionPlan.misconception'
                            editContent={editingState.key === 'correctiveInstructionPlan.misconception' ? editingState.content : lessonContent.correctiveInstructionPlan?.misconception}
                            isEditing={editingState.key === 'correctiveInstructionPlan.misconception'}
                            isAdapted={adaptedKeys.has('correctiveInstructionPlan.misconception')}
                            onEdit={isEditMode ? handleEdit : undefined}
                            onChange={isEditMode ? handleContentChange : undefined}
                            onSave={isEditMode ? handleSave : undefined}
                            onCancel={isEditMode ? handleCancel : undefined}
                            onDelete={isEditMode ? () => handleClearField('correctiveInstructionPlan.misconception', 'Common Misconception') : undefined}
                            deleteTooltip="Clear misconception"
                        >
                            <Paragraph><strong>Common Misconception:</strong> {lessonContent.correctiveInstructionPlan?.misconception || <Text type="secondary" italic>Not specified</Text>}</Paragraph>
                        </EditableSectionDevelopment>
                        <EditableSectionDevelopment
                            contentKey='correctiveInstructionPlan.remediation'
                            editContent={editingState.key === 'correctiveInstructionPlan.remediation' ? editingState.content : lessonContent.correctiveInstructionPlan?.remediation}
                            isEditing={editingState.key === 'correctiveInstructionPlan.remediation'}
                            isAdapted={adaptedKeys.has('correctiveInstructionPlan.remediation')}
                            onEdit={isEditMode ? handleEdit : undefined}
                            onChange={isEditMode ? handleContentChange : undefined}
                            onSave={isEditMode ? handleSave : undefined}
                            onCancel={isEditMode ? handleCancel : undefined}
                            onDelete={isEditMode ? () => handleClearField('correctiveInstructionPlan.remediation', 'Remediation Plan') : undefined}
                            deleteTooltip="Clear remediation plan"
                        >
                            <Paragraph><strong>Remediation Plan:</strong> {lessonContent.correctiveInstructionPlan?.remediation || <Text type="secondary" italic>Not specified</Text>}</Paragraph>
                        </EditableSectionDevelopment>
                    </Panel>
                )}
                {showComponents.has('pitfalls_indicators') && (
                    <>
                        <Panel
                            header={<Space><WarningOutlined />Common Pitfalls</Space>}
                            key="2"
                            extra={
                                isEditMode && (
                                    <Button
                                        type="primary"
                                        shape="circle"
                                        icon={<PlusOutlined />}
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleAddListItem('commonPitfalls', 'New pitfall. Click to edit.', 'Pitfall');
                                        }}
                                        title="Add Pitfall"
                                    />
                                )
                            }
                        >
                            <List
                                dataSource={lessonContent.commonPitfalls || []}
                                renderItem={(item, itemIndex) => (
                                    <List.Item key={itemIndex}>
                                        {(() => {
                                            const contentKey = `commonPitfalls.${itemIndex}`;
                                            const isEditingThisOne = editingState.key === contentKey;
                                            return (
                                                <EditableSectionDevelopment
                                                    contentKey={contentKey}
                                                    editContent={isEditingThisOne ? editingState.content : item}
                                                    isEditing={isEditingThisOne}
                                                    isAdapted={adaptedKeys.has(contentKey)}
                                                    onEdit={isEditMode ? handleEdit : undefined}
                                                    onChange={isEditMode ? handleContentChange : undefined}
                                                    onSave={isEditMode ? handleSave : undefined}
                                                    onCancel={isEditMode ? handleCancel : undefined}
                                                    onDelete={isEditMode ? () => handleRemoveListItem('commonPitfalls', itemIndex, item) : undefined}
                                                    deleteTooltip="Remove pitfall"
                                                >
                                                    <Text>{item}</Text>
                                                </EditableSectionDevelopment>
                                            );
                                        })()}
                                    </List.Item>
                                )}
                            />
                        </Panel>
                        <Panel
                            header={<Space><CheckSquareOutlined />Success Indicators</Space>}
                            key="3"
                            extra={
                                isEditMode && (
                                    <Button
                                        type="primary"
                                        shape="circle"
                                        icon={<PlusOutlined />}
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleAddListItem('successIndicators', 'New indicator. Click to edit.', 'Indicator');
                                        }}
                                        title="Add Indicator"
                                    />
                                )
                            }
                        >
                            <List
                                dataSource={lessonContent.successIndicators || []}
                                renderItem={(item, itemIndex) => (
                                    <List.Item key={itemIndex}>
                                        {(() => {
                                            const contentKey = `successIndicators.${itemIndex}`;
                                            const isEditingThisOne = editingState.key === contentKey;
                                            return (
                                                <EditableSectionDevelopment
                                                    contentKey={contentKey}
                                                    editContent={isEditingThisOne ? editingState.content : item}
                                                    isEditing={isEditingThisOne}
                                                    isAdapted={adaptedKeys.has(contentKey)}
                                                    onEdit={isEditMode ? handleEdit : undefined}
                                                    onChange={isEditMode ? handleContentChange : undefined}
                                                    onSave={isEditMode ? handleSave : undefined}
                                                    onCancel={isEditMode ? handleCancel : undefined}
                                                    onDelete={isEditMode ? () => handleRemoveListItem('successIndicators', itemIndex, item) : undefined}
                                                    deleteTooltip="Remove indicator"
                                                >
                                                    <Text>{item}</Text>
                                                </EditableSectionDevelopment>
                                            );
                                        })()}
                                    </List.Item>
                                )}
                            />
                        </Panel>
                    </>
                )}
                {showComponents.has('planning_notes') && (
                    <Panel
                        header={<Space><ToolOutlined />Adaptation Notes</Space>}
                        key="4"
                        extra={
                            isEditMode && (
                                <Button
                                    type="primary"
                                    shape="circle"
                                    icon={<PlusOutlined />}
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleAddListItem('adaptationNotes', 'New note. Click to edit.', 'Note');
                                    }}
                                    title="Add Note"
                                />
                            )
                        }
                    >
                        <List
                            dataSource={lessonContent.adaptationNotes || []}
                            renderItem={(item, itemIndex) => (
                                <List.Item key={itemIndex}>
                                    {(() => {
                                        const contentKey = `adaptationNotes.${itemIndex}`;
                                        const isEditingThisOne = editingState.key === contentKey;
                                        return (
                                            <EditableSectionDevelopment
                                                contentKey={contentKey}
                                                editContent={isEditingThisOne ? editingState.content : item}
                                                isEditing={isEditingThisOne}
                                                isAdapted={adaptedKeys.has(contentKey)}
                                                onEdit={isEditMode ? handleEdit : undefined}
                                                onChange={isEditMode ? handleContentChange : undefined}
                                                onSave={isEditMode ? handleSave : undefined}
                                                onCancel={isEditMode ? handleCancel : undefined}
                                                onDelete={isEditMode ? () => handleRemoveListItem('adaptationNotes', itemIndex, item) : undefined}
                                                deleteTooltip="Remove note"
                                            >
                                                <Text>{item}</Text>
                                            </EditableSectionDevelopment>
                                        );
                                    })()}
                                </List.Item>
                            )}
                        />
                    </Panel>
                )}
            </Collapse>
        </div>
    );
};

export default PlanningConsiderationsSection;
